import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, RefreshCw, Check, Trash2, Sparkles, Download } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import { Button } from '../components/ui/button';

const TYPE_LABELS = {
  blog_post: 'Blog',
  social_instagram: 'Instagram',
  social_linkedin: 'LinkedIn',
};

const STATUS_LABELS = { DRAFT: 'Borrador', APPROVED: 'Aprobado', DISCARDED: 'Descartado' };

const STATUS_COLORS = {
  DRAFT: 'bg-amber-100 text-amber-800',
  APPROVED: 'bg-emerald-100 text-emerald-800',
  DISCARDED: 'bg-gray-200 text-gray-500',
};

// Aterrizaje MDX (Commit 6): genera el archivo .mdx con el mismo frontmatter
// que los posts reales de adamsched-web/content/blog (title, description,
// date, slug, category, keywords — gray-matter, cargado por lib/blog.ts).
// Marta lo descarga y lo coloca en una rama de adamsched-web → PR → preview
// de Vercel como revisión final. Cero automatización cross-repo.
function downloadMdx(draft, topic) {
  const esc = (s) => String(s || '').replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  const today = new Date().toISOString().slice(0, 10);
  const keywords = topic?.target_keyword ? [topic.target_keyword] : [];
  const frontmatter = [
    '---',
    `title: "${esc(draft.title)}"`,
    `description: "${esc(draft.meta_description)}"`,
    `date: "${today}"`,
    `slug: "${esc(draft.slug_suggested)}"`,
    'category: "Gestión"',
    `keywords: [${keywords.map((k) => `"${esc(k)}"`).join(', ')}]`,
    '---',
    '',
  ].join('\n');

  const blob = new Blob([frontmatter + draft.body + '\n'], {
    type: 'text/markdown;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${draft.slug_suggested || draft.id}.mdx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// Dashboard del Adam Content Engine (Commit 4). Revisión de borradores:
// listar, editar inline, aprobar/descartar/regenerar. Aprobar SOLO cambia el
// status en la base de datos — no publica nada en ningún canal (regla de la
// spec: cero publicación automática, sin excepción).
export function ContentEnginePage() {
  const { api } = useAuth();
  const { t } = useTranslation();

  const [drafts, setDrafts] = useState([]);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('DRAFT');
  const [editing, setEditing] = useState(null); // {id, title, meta_description, body}
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (typeFilter) params.type = typeFilter;
      if (statusFilter) params.status = statusFilter;
      const [draftsRes, topicsRes] = await Promise.all([
        api.get('/content-engine/drafts', { params }),
        api.get('/content-engine/topics'),
      ]);
      setDrafts(draftsRes.data);
      setTopics(topicsRes.data);
    } catch (e) {
      toast.error(t('success.contentEngine.draftsLoadError'));
    } finally {
      setLoading(false);
    }
  }, [api, typeFilter, statusFilter, t]);

  useEffect(() => { load(); }, [load]);

  const topicById = Object.fromEntries(topics.map((t) => [t.id, t]));
  const counts = topics.reduce((acc, t) => {
    acc[t.status] = (acc[t.status] || 0) + 1;
    return acc;
  }, {});

  const act = async (draftId, action, successMsg) => {
    setBusyId(draftId);
    try {
      await api.post(`/content-engine/drafts/${draftId}/${action}`);
      toast.success(successMsg);
      await load();
    } catch (e) {
      toast.error(`Error en ${action}`);
    } finally {
      setBusyId(null);
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    setBusyId(editing.id);
    try {
      const patch = {
        body: editing.body,
        ...(editing.title != null ? { title: editing.title } : {}),
        ...(editing.meta_description != null ? { meta_description: editing.meta_description } : {}),
      };
      await api.patch(`/content-engine/drafts/${editing.id}`, patch);
      toast.success(t('success.contentEngine.draftSaved'));
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(t('success.contentEngine.editSaveError'));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6 h-full overflow-y-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-violet-600" /> Adam Content Engine
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Borradores de blog y redes. Nada se publica automáticamente: aprobar solo marca la
            pieza como lista.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className="w-4 h-4 mr-1" /> Recargar
        </Button>
      </div>

      <div className="flex gap-4 text-sm text-gray-600">
        <span>Cola de temas:</span>
        <span>⏳ pendientes {counts.PENDING || 0}</span>
        <span>✍️ con borrador {counts.DRAFTED || 0}</span>
        {counts.ERROR ? <span className="text-red-600">⚠️ con error {counts.ERROR}</span> : null}
      </div>

      <div className="flex gap-3">
        <select
          className="border rounded-md px-3 py-1.5 text-sm"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
        >
          <option value="">Todos los tipos</option>
          <option value="blog_post">Blog</option>
          <option value="social_instagram">Instagram</option>
          <option value="social_linkedin">LinkedIn</option>
        </select>
        <select
          className="border rounded-md px-3 py-1.5 text-sm"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="DRAFT">Pendientes de revisar</option>
          <option value="APPROVED">Aprobados</option>
          <option value="DISCARDED">Descartados</option>
          <option value="">Todos</option>
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      ) : drafts.length === 0 ? (
        <p className="text-center text-gray-500 py-16">
          No hay borradores con estos filtros. El job semanal genera nuevos desde la cola de temas.
        </p>
      ) : (
        <div className="space-y-4">
          {drafts.map((d) => {
            const topic = topicById[d.topic_id];
            const isEditing = editing?.id === d.id;
            const busy = busyId === d.id;
            return (
              <div key={d.id} className="border rounded-lg p-4 bg-white shadow-sm">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold px-2 py-0.5 rounded bg-violet-100 text-violet-700">
                      {TYPE_LABELS[d.type] || d.type}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded ${STATUS_COLORS[d.status] || ''}`}>
                      {STATUS_LABELS[d.status] || d.status}
                    </span>
                    <span className="text-xs text-gray-400">{d.model} · {d.prompt_version}</span>
                  </div>
                  <div className="flex gap-2">
                    {d.status === 'DRAFT' && !isEditing && (
                      <>
                        <Button size="sm" variant="outline" disabled={busy}
                          onClick={() => setEditing({
                            id: d.id, body: d.body,
                            title: d.title, meta_description: d.meta_description,
                          })}>
                          Editar
                        </Button>
                        <Button size="sm" disabled={busy}
                          onClick={() => act(d.id, 'approve', 'Aprobado — listo para publicar a mano')}>
                          <Check className="w-4 h-4 mr-1" /> Aprobar
                        </Button>
                        <Button size="sm" variant="outline" disabled={busy}
                          onClick={() => act(d.id, 'regenerate', 'Regenerado — nuevo borrador creado')}>
                          <RefreshCw className="w-4 h-4 mr-1" /> Regenerar
                        </Button>
                        <Button size="sm" variant="destructive" disabled={busy}
                          onClick={() => act(d.id, 'discard', 'Descartado')}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                    {isEditing && (
                      <>
                        <Button size="sm" onClick={saveEdit} disabled={busy}>Guardar</Button>
                        <Button size="sm" variant="outline" onClick={() => setEditing(null)}>
                          Cancelar
                        </Button>
                      </>
                    )}
                    {d.status === 'APPROVED' && d.type === 'blog_post' && (
                      <Button size="sm" variant="outline"
                        onClick={() => {
                          downloadMdx(d, topic);
                          toast.success(t('success.contentEngine.mdxDownloaded'));
                        }}>
                        <Download className="w-4 h-4 mr-1" /> Descargar .mdx
                      </Button>
                    )}
                  </div>
                </div>

                {topic && (
                  <p className="text-xs text-gray-500 mt-2">
                    Tema: {topic.topic}
                    {topic.target_keyword ? ` · kw: ${topic.target_keyword}` : ''}
                  </p>
                )}

                {isEditing ? (
                  <div className="mt-3 space-y-2">
                    {d.type === 'blog_post' && (
                      <>
                        <input
                          className="w-full border rounded-md px-3 py-1.5 text-sm"
                          value={editing.title || ''}
                          onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                          placeholder="Título"
                        />
                        <input
                          className="w-full border rounded-md px-3 py-1.5 text-sm"
                          value={editing.meta_description || ''}
                          onChange={(e) => setEditing({ ...editing, meta_description: e.target.value })}
                          placeholder="Meta description"
                        />
                      </>
                    )}
                    <textarea
                      className="w-full border rounded-md px-3 py-2 text-sm font-mono"
                      rows={d.type === 'blog_post' ? 20 : 8}
                      value={editing.body}
                      onChange={(e) => setEditing({ ...editing, body: e.target.value })}
                    />
                  </div>
                ) : (
                  <div className="mt-3">
                    {d.title && <h3 className="font-semibold">{d.title}</h3>}
                    {d.meta_description && (
                      <p className="text-xs text-gray-500 italic mt-0.5">{d.meta_description}</p>
                    )}
                    <pre className="mt-2 text-sm whitespace-pre-wrap font-sans text-gray-800 max-h-64 overflow-y-auto border-t pt-2">
                      {d.body}
                    </pre>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
